import React, { useState } from "react";
import { ArrowLeftRight, Gift, Loader2, SlidersHorizontal, Undo2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDialog from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/billing";

const METHODS = ["e-transfer", "cash", "cheque", "other"];

function Block({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Icon className="h-4 w-4 text-slate-500" /> {title}</div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">{children}</div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wide text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function PoolSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1 min-h-[40px]" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="enrichment">Enrichment</SelectItem>
        <SelectItem value="scrape">Scrape</SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function UserBillingActions({ user, data, onChanged }) {
  const [busy, setBusy] = useState("");
  const [grant, setGrant] = useState({ months: "1", method: "e-transfer", note: "" });
  const [adj, setAdj] = useState({ pool: "enrichment", delta: "", note: "" });
  const [swap, setSwap] = useState({ from_pool: "enrichment", to_pool: "scrape", amount: "", note: "" });
  const [confirmCancel, setConfirmCancel] = useState(false);
  const st = data.status.status;
  // Show cancel/resume whenever a paid period is running, even while global billing is off.
  const periodRunning = !!data.status.period_end && new Date(data.status.period_end) > new Date();
  const scheduledCancel = !!data.status.cancel_at_period_end;
  const showSubControls = st === "active" || st === "cancelling" || periodRunning;
  const isCancelling = st === "cancelling" || (st !== "active" && scheduledCancel);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      onChanged?.();
    } catch (e) {
      toast.error("Action failed", { description: formatApiError(e) });
    } finally {
      setBusy("");
    }
  };

  const doGrant = () => run("grant", () => api.post(`/admin/billing/users/${user.id}/grant`, { months: Number(grant.months), method: grant.method, note: grant.note }), `${grant.months} month(s) granted — receipt issued`);
  const doAdjust = () => run("adjust", () => api.post(`/admin/billing/users/${user.id}/adjust-credits`, { pool: adj.pool, delta: Number(adj.delta), note: adj.note }), "Credits adjusted");
  const doSwap = () => run("swap", () => api.post(`/admin/billing/users/${user.id}/swap-credits`, { from_pool: swap.from_pool, to_pool: swap.to_pool, amount: Number(swap.amount), note: swap.note }), "Credits swapped");
  const doCancel = () => run("cancel", () => api.post(`/admin/billing/users/${user.id}/cancel`), "Subscription will end at the current period end");
  const doResume = () => run("resume", () => api.post(`/admin/billing/users/${user.id}/resume`), "Auto-renewal resumed");

  return (
    <div className="space-y-3" data-testid="user-billing-actions">
      <Block title="Grant months (paid outside the app)" icon={Gift} testid="user-billing-grant">
        <F label="Months">
          <Select value={grant.months} onValueChange={(v) => setGrant({ ...grant, months: v })}>
            <SelectTrigger className="mt-1 min-h-[40px]" data-testid="user-billing-grant-months"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 18 }, (_, i) => String(i + 1)).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Paid by">
          <Select value={grant.method} onValueChange={(v) => setGrant({ ...grant, method: v })}>
            <SelectTrigger className="mt-1 min-h-[40px]" data-testid="user-billing-grant-method"><SelectValue /></SelectTrigger>
            <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Note"><Input value={grant.note} onChange={(e) => setGrant({ ...grant, note: e.target.value })} placeholder="e.g. e-transfer #1234" className="mt-1 min-h-[40px]" data-testid="user-billing-grant-note" /></F>
        <Button onClick={doGrant} disabled={!!busy} className="min-h-[40px] text-white" style={{ background: "hsl(var(--brand))" }} data-testid="user-billing-grant-submit">
          {busy === "grant" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Grant
        </Button>
      </Block>

      <Block title="Adjust credits" icon={SlidersHorizontal} testid="user-billing-adjust">
        <F label="Pool"><PoolSelect value={adj.pool} onChange={(v) => setAdj({ ...adj, pool: v })} testid="user-billing-adjust-pool" /></F>
        <F label="± credits"><Input type="number" value={adj.delta} onChange={(e) => setAdj({ ...adj, delta: e.target.value })} placeholder="e.g. 500 or -200" className="mt-1 min-h-[40px]" data-testid="user-billing-adjust-delta" /></F>
        <F label="Reason (required)"><Input value={adj.note} onChange={(e) => setAdj({ ...adj, note: e.target.value })} className="mt-1 min-h-[40px]" data-testid="user-billing-adjust-note" /></F>
        <Button variant="outline" onClick={doAdjust} disabled={!!busy || !Number(adj.delta) || !adj.note.trim()} className="min-h-[40px]" data-testid="user-billing-adjust-submit">
          {busy === "adjust" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Apply
        </Button>
      </Block>

      <Block title="Swap credits between pools" icon={ArrowLeftRight} testid="user-billing-swap">
        <F label="From"><PoolSelect value={swap.from_pool} onChange={(v) => setSwap({ ...swap, from_pool: v, to_pool: v === "enrichment" ? "scrape" : "enrichment" })} testid="user-billing-swap-from" /></F>
        <F label="Amount"><Input type="number" min={1} value={swap.amount} onChange={(e) => setSwap({ ...swap, amount: e.target.value })} className="mt-1 min-h-[40px]" data-testid="user-billing-swap-amount" /></F>
        <F label="Note"><Input value={swap.note} onChange={(e) => setSwap({ ...swap, note: e.target.value })} placeholder="Bought the wrong pack" className="mt-1 min-h-[40px]" data-testid="user-billing-swap-note" /></F>
        <Button variant="outline" onClick={doSwap} disabled={!!busy || !Number(swap.amount)} className="min-h-[40px]" data-testid="user-billing-swap-submit">
          {busy === "swap" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Swap → {swap.to_pool} (×{data.plan?.swap_rate ?? 1})
        </Button>
      </Block>

      {showSubControls ? (
        <div className="rounded-md border border-slate-200 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="user-billing-subscription-controls">
          <p className="text-xs text-slate-600">
            {!isCancelling
              ? `Auto-renews on ${fmtDate(data.status.period_end)}. Cancelling keeps access until then; no further charges.`
              : `Cancellation scheduled — access ends ${fmtDate(data.status.period_end)}.`}
          </p>
          {!isCancelling ? (
            <Button variant="ghost" onClick={() => setConfirmCancel(true)} disabled={!!busy} className="min-h-[40px] text-rose-700" data-testid="user-billing-cancel">
              <XCircle className="h-4 w-4 mr-1.5" /> Cancel at period end
            </Button>
          ) : (
            <Button variant="outline" onClick={doResume} disabled={!!busy} className="min-h-[40px]" data-testid="user-billing-resume">
              {busy === "resume" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1.5" />} Resume auto-renewal
            </Button>
          )}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmCancel}
        title={`Cancel ${user.email}'s subscription?`}
        description={`They keep full access until ${fmtDate(data.status.period_end)} (already paid). No renewal will be charged after that. Their data is never deleted.`}
        confirmLabel="Cancel at period end"
        destructive
        onConfirm={() => { setConfirmCancel(false); doCancel(); }}
        onClose={() => setConfirmCancel(false)}
        testid="user-billing-cancel-confirm"
      />
    </div>
  );
}
